/**
 * The club, as a thing you can see and edit.
 *
 * This screen exists because "where does this money belong" is a question
 * about the building, not about the chart of accounts, and until now the
 * only answer was a migration.
 *
 * Two ideas are made visible rather than explained in a tooltip:
 *
 *   - Headings are drawn differently and cannot be chosen anywhere a
 *     posting is chosen. A department with children stops accepting
 *     money; the interface should never offer what the database will
 *     refuse.
 *   - An outlet is shown inside its department, because after migration
 *     0026 the outlet is what decides where a charge posts. Moving the
 *     halfway house moves golf's food revenue, and the only way to make
 *     that obvious is to draw it that way.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Outlet {
  id: string;
  code: string;
  name: string;
  department_id: string;
  source_module: string;
  is_active: boolean;
  charge_count: number;
}

interface Department {
  id: string;
  parent_id: string | null;
  code: string;
  name: string;
  kind: string;
  depth: number;
  path: string;
  is_leaf: boolean;
  is_active: boolean;
  is_postable: boolean;
  outlet_count: number;
  outlets: Outlet[];
}

export function StructurePage() {
  const { atLeast } = useSession();
  const canEdit = atLeast('club_admin');

  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState<{ kind: 'department' | 'outlet'; parent: Department } | null>(
    null,
  );
  const [editing, setEditing] = useState<Department | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const structure = useApi<{ departments: Department[] }>(
    `/v1/structure?include_inactive=${showInactive}`,
    [showInactive],
  );

  const refresh = (message?: string | null) => {
    setAdding(null);
    setEditing(null);
    setNote(message ?? null);
    structure.reload();
  };

  const departments = structure.data?.departments ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>The Club</h1>
          <div className="muted small">
            Every department and every place that can ring up a charge.
          </div>
        </div>
        <div className="spacer" />
        <label
          className="muted small"
          style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show closed
        </label>
        {canEdit && (
          <button onClick={() => setAdding({ kind: 'department', parent: null as never })}>
            Add a top-level department
          </button>
        )}
      </div>

      <ErrorBanner error={structure.error} />

      {note && (
        <Card>
          <Status tone="warning">{note}</Status>
        </Card>
      )}

      {adding && (
        <AddForm
          kind={adding.kind}
          parent={adding.parent}
          departments={departments}
          onDone={refresh}
          onCancel={() => setAdding(null)}
        />
      )}

      {editing && (
        <EditDepartmentForm
          department={editing}
          departments={departments}
          onDone={refresh}
          onCancel={() => setEditing(null)}
        />
      )}

      <Card flush>
        {structure.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={8} />
          </div>
        ) : departments.length === 0 ? (
          <Empty>This club has no departments yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Department &amp; outlets</th>
                <th>Code</th>
                {/*
                  One column, two meanings, and they have to stay legible
                  side by side: a department either takes postings or is a
                  heading, and an outlet says which till its charges come
                  from. Labelling it "Takes charges" made the outlet rows
                  read as if 'dining_room' were an answer to that question.
                */}
                <th>Postings</th>
                <th className="num">Activity</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {departments.map((d) => (
                <DepartmentRows
                  key={d.id}
                  department={d}
                  canEdit={canEdit}
                  onAddChild={() => setAdding({ kind: 'department', parent: d })}
                  onAddOutlet={() => setAdding({ kind: 'outlet', parent: d })}
                  onEdit={() => setEditing(d)}
                  onChanged={refresh}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="How This Works">
        <ul className="muted small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          <li>
            A department with others beneath it becomes a <strong>heading</strong>. Headings hold
            the totals of everything under them and take no charges of their own — that is what
            makes the totals add up.
          </li>
          <li>
            An outlet decides where its charges land. The same club sandwich posts to the dining
            room, the halfway house or the pool snack shop depending only on where it was rung up.
          </li>
          <li>
            Moving an outlet changes where <em>future</em> charges post. Last month's figures stay
            where they were, because last month's statements have already been sent.
          </li>
          <li>Nothing is ever deleted. A closed department keeps its history and its reports.</li>
        </ul>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function DepartmentRows({
  department,
  canEdit,
  onAddChild,
  onAddOutlet,
  onEdit,
  onChanged,
}: {
  department: Department;
  canEdit: boolean;
  onAddChild: () => void;
  onAddOutlet: () => void;
  onEdit: () => void;
  onChanged: (note?: string | null) => void;
}) {
  const d = department;
  const indent = 8 + d.depth * 22;

  return (
    <>
      <tr className={d.is_active ? undefined : 'muted'}>
        <td style={{ paddingLeft: indent }}>
          {d.is_postable ? (
            <span>{d.name}</span>
          ) : (
            <strong style={{ letterSpacing: '0.02em' }}>{d.name}</strong>
          )}
          {!d.is_active && <span className="muted small"> · closed</span>}
        </td>
        <td className="mono muted small">{d.code}</td>
        <td>
          {d.is_postable ? (
            <Status tone="good">Takes charges</Status>
          ) : (
            <Status tone="neutral">Heading — totals only</Status>
          )}
        </td>
        <td className="num muted">
          {d.outlets.length ? `${d.outlets.length} outlet${d.outlets.length === 1 ? '' : 's'}` : '—'}
        </td>
        {canEdit && (
          <td className="num" style={{ whiteSpace: 'nowrap' }}>
            <button className="secondary small" onClick={onEdit}>
              Edit
            </button>{' '}
            <button className="secondary small" onClick={onAddChild}>
              + Department
            </button>{' '}
            {d.is_postable && (
              <button className="secondary small" onClick={onAddOutlet}>
                + Outlet
              </button>
            )}
          </td>
        )}
      </tr>

      {d.outlets.map((o) => (
        <OutletRow
          key={o.id}
          outlet={o}
          indent={indent + 22}
          canEdit={canEdit}
          onChanged={onChanged}
        />
      ))}
    </>
  );
}

function OutletRow({
  outlet,
  indent,
  canEdit,
  onChanged,
}: {
  outlet: Outlet;
  indent: number;
  canEdit: boolean;
  onChanged: (note?: string | null) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(outlet.name);

  const save = useMutation(async (body: Record<string, unknown>) =>
    api<{ note: string | null }>(`/v1/structure/outlets/${outlet.id}`, { method: 'PATCH', body }),
  );

  return (
    <tr className={outlet.is_active ? undefined : 'muted'}>
      <td style={{ paddingLeft: indent }}>
        {renaming ? (
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 240 }} />
        ) : (
          <span className="muted">↳ {outlet.name}</span>
        )}
        {!outlet.is_active && <span className="muted small"> · closed</span>}
      </td>
      <td className="mono muted small">{outlet.code}</td>
      <td className="muted small">
        Outlet · charges arrive as <span className="mono">{outlet.source_module}</span>
      </td>
      <td className="num muted small">
        {outlet.charge_count > 0
          ? `${outlet.charge_count} charge${outlet.charge_count === 1 ? '' : 's'}`
          : 'no charges yet'}
      </td>
      {canEdit && (
        <td className="num" style={{ whiteSpace: 'nowrap' }}>
          {renaming ? (
            <>
              <button
                className="small"
                disabled={save.busy}
                onClick={async () => {
                  const r = await save.run({ name });
                  if (r) {
                    setRenaming(false);
                    onChanged(r.note);
                  }
                }}
              >
                Save
              </button>{' '}
              <button className="secondary small" onClick={() => setRenaming(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="secondary small" onClick={() => setRenaming(true)}>
                Rename
              </button>{' '}
              <button
                className="secondary small"
                disabled={save.busy}
                onClick={async () => {
                  const r = await save.run({ is_active: !outlet.is_active });
                  if (r) onChanged(null);
                }}
              >
                {outlet.is_active ? 'Close' : 'Reopen'}
              </button>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

/* ------------------------------------------------------------------ */

function AddForm({
  kind,
  parent,
  departments,
  onDone,
  onCancel,
}: {
  kind: 'department' | 'outlet';
  parent: Department | null;
  departments: Department[];
  onDone: (note?: string | null) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sourceModule, setSourceModule] = useState('other');
  const [departmentId, setDepartmentId] = useState(parent?.id ?? '');

  const create = useMutation(async (body: Record<string, unknown>) =>
    api<{ note?: string | null }>(
      kind === 'department' ? '/v1/structure/departments' : '/v1/structure/outlets',
      { method: 'POST', body },
    ),
  );

  // A form that offers a heading is a form that produces an error the
  // database has already decided to give. Only leaves are offered.
  const postable = departments.filter((d) => d.is_postable && d.is_active);

  return (
    <Card
      title={
        kind === 'department'
          ? parent
            ? `New department under ${parent.name}`
            : 'New top-level department'
          : `New outlet in ${parent?.name}`
      }
    >
      <ErrorBanner error={create.error} />

      <div className="row">
        <label>
          <span className="muted small">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={kind === 'department' ? 'Skeet Range' : 'Halfway House'}
            autoFocus
          />
        </label>
        <label>
          <span className="muted small">Code</span>
          <input
            value={code}
            onChange={(e) =>
              setCode(kind === 'outlet' ? e.target.value.toUpperCase() : e.target.value)
            }
            placeholder={kind === 'department' ? '55' : 'SKEET'}
            style={{ maxWidth: 120 }}
            className="mono"
          />
        </label>

        {kind === 'outlet' && (
          <>
            <label>
              <span className="muted small">Department</span>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                {postable.map((d) => (
                  <option key={d.id} value={d.id}>
                    {' '.repeat(d.depth * 2)}
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="muted small">Ordering system</span>
              <input
                value={sourceModule}
                onChange={(e) => setSourceModule(e.target.value.toLowerCase())}
                placeholder="pool_ordering"
                className="mono"
                style={{ maxWidth: 180 }}
              />
            </label>
          </>
        )}
      </div>

      {kind === 'outlet' && (
        <p className="muted small" style={{ marginTop: 8 }}>
          The ordering system is how charges from this outlet are tagged when they arrive. Two
          separate tills must not share one — it is what stops a retry from one being mistaken for
          a duplicate of the other.
        </p>
      )}

      {kind === 'department' && parent && parent.is_postable && (
        <p className="muted small" style={{ marginTop: 8 }}>
          Adding this will turn <strong>{parent.name}</strong> into a heading. It will hold the
          total of everything beneath it and stop taking charges of its own.
        </p>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="gold"
          disabled={create.busy || !name || !code || (kind === 'outlet' && !departmentId)}
          onClick={async () => {
            const r = await create.run(
              kind === 'department'
                ? { code, name, parent_id: parent?.id ?? null }
                : { code, name, department_id: departmentId, source_module: sourceModule },
            );
            if (r) onDone(r.note ?? null);
          }}
        >
          {create.busy ? 'Saving…' : 'Add'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

function EditDepartmentForm({
  department,
  departments,
  onDone,
  onCancel,
}: {
  department: Department;
  departments: Department[];
  onDone: (note?: string | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(department.name);
  const [parentId, setParentId] = useState(department.parent_id ?? '');

  const usage = useApi<{
    journal_line_count: number;
    outlet_count: number;
    revenue_category_count: number;
    account_count: number;
    child_count: number;
  }>(`/v1/structure/departments/${department.id}/usage`);

  const save = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/structure/departments/${department.id}`, { method: 'PATCH', body }),
  );

  // Nothing may become its own descendant. The tree view already carries
  // the path, so the check is a string one rather than a walk.
  const candidates = departments.filter(
    (d) => d.id !== department.id && !d.path.startsWith(`${department.path} › `),
  );

  return (
    <Card title={`Edit ${department.name}`}>
      <ErrorBanner error={save.error} />

      <div className="row">
        <label>
          <span className="muted small">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label>
          <span className="muted small">Sits under</span>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">(top level)</option>
            {candidates.map((d) => (
              <option key={d.id} value={d.id}>
                {' '.repeat(d.depth * 2)}
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {usage.data && (
        <p className="muted small" style={{ marginTop: 10 }}>
          {department.name} currently carries {usage.data.journal_line_count} ledger line
          {usage.data.journal_line_count === 1 ? '' : 's'}, {usage.data.outlet_count} outlet
          {usage.data.outlet_count === 1 ? '' : 's'}, {usage.data.revenue_category_count} revenue
          categor{usage.data.revenue_category_count === 1 ? 'y' : 'ies'} and{' '}
          {usage.data.account_count} account default
          {usage.data.account_count === 1 ? '' : 's'}.
          {usage.data.journal_line_count > 0 &&
            ' Because it has postings, it cannot become a heading until they are moved.'}
        </p>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="gold"
          disabled={save.busy}
          onClick={async () => {
            const r = await save.run({ name, parent_id: parentId || null });
            if (r) onDone(null);
          }}
        >
          {save.busy ? 'Saving…' : 'Save'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <div className="spacer" />
        <button
          className="secondary"
          disabled={save.busy}
          onClick={async () => {
            const r = await save.run({ is_active: !department.is_active });
            if (r) onDone(null);
          }}
        >
          {department.is_active ? 'Close this department' : 'Reopen'}
        </button>
      </div>
    </Card>
  );
}
